/**
 * AdminIntegrations — Manage integrations catalog (D1 source of truth)
 */
import { useState, useEffect, useRef } from 'preact/hooks';
import { isAdmin } from '../stores/auth.store';
import { api } from '../api/client';

import { T } from '../styles/tokens';
import { Button, ListSkeleton, SkeletonBar, Textarea } from '../components/ui';
import i18next from 'i18next';
interface Integration {
  id: string;
  label: string;
  category: string;
  color: string;
  description?: string;
  website?: string;
  svg_path?: string;
  env_keys?: string;
  sort_order: number;
  enabled: number;
  is_favorite?: number;
  created_at?: string;
  updated_at?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  cloud: i18next.t('admin:integ_cloud'),
  dev: i18next.t('admin:integ_dev'),
  messaging: i18next.t('admin:integ_messaging'),
  email: i18next.t('admin:integ_email'),
  payment: i18next.t('admin:integ_payment'),
  monitoring: i18next.t('admin:integ_monitoring'),
  marketing: i18next.t('admin:integ_marketing'),
  ai: i18next.t('admin:integ_ai'),
  ecommerce: 'E-commerce',
  storage: i18next.t('admin:integ_storage'),
  support: i18next.t('admin:integ_support'),
  other: i18next.t('admin:integ_other'),
};

const CATEGORY_ORDER = ['cloud', 'dev', 'messaging', 'email', 'payment', 'monitoring', 'marketing', 'ai', 'ecommerce', 'storage', 'support', 'other'];

function SvgPreview({ path, color, size = 24 }: { path?: string; color: string; size?: number }) {
  if (!path) return <div style={{ width: size, height: size, borderRadius: 4, background: T.borderHover }} />;
  const isStroke = !path.includes('c') && !path.includes('C') && path.includes('l');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill={isStroke ? 'none' : color} stroke={isStroke ? color : 'none'}
      stroke-width={isStroke ? '2' : '0'} stroke-linecap="round" stroke-linejoin="round">
      <path d={path} />
    </svg>
  );
}

function CategoryBadge({ category }: { category: string }) {
  const colors: Record<string, string> = {
    cloud: '#F38020', dev: T.text, messaging: '#25D366', email: T.danger,
    payment: '#635BFF', monitoring: T.success, marketing: '#FF642D', ai: '#D4A574',
    ecommerce: '#7AB55C', storage: T.primary, support: '#03363D',
  };
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
      color: colors[category] || T.textMuted,
      background: `${colors[category] || T.textMuted}15`,
      border: `1px solid ${colors[category] || T.textMuted}30`,
    }}>
      {CATEGORY_LABELS[category] || category}
    </span>
  );
}

export function AdminIntegrations() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<Integration>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ ok: boolean; msg: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  if (!isAdmin.value) {
    return <div style={{ padding: 40, color: T.danger, textAlign: 'center' }}>Acesso restrito a administradores.</div>;
  }

  async function load() {
    setLoading(true);
    try {
      const res = await api.get('/admin/integrations-catalog') as any;
      setIntegrations(res?.integrations || []);
    } catch (e: any) {
      showToast(false, 'Erro ao carregar: ' + (e?.message || 'desconhecido'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function showToast(ok: boolean, msg: string) {
    setToast({ ok, msg });
    setTimeout(() => setToast(null), 4000);
  }

  function startEdit(integ: Integration) {
    setEditing(integ.id);
    setEditData({ ...integ });
    setCreating(false);
  }

  function startCreate() {
    setEditing(null);
    setCreating(true);
    setEditData({ id: '', label: '', category: 'cloud', color: T.textMuted, enabled: 1, sort_order: integrations.length });
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (creating) {
        await api.post('/admin/integrations-catalog', editData);
        showToast(true, `Integração "${editData.label}" criada!`);
      } else if (editing) {
        await api.put(`/admin/integrations-catalog/${editing}`, editData);
        showToast(true, `Integração "${editData.label}" atualizada!`);
      }
      setEditing(null);
      setCreating(false);
      setEditData({});
      await load();
    } catch (e: any) {
      showToast(false, 'Erro: ' + (e?.message || 'desconhecido'));
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(integ: Integration) {
    try {
      const newEnabled = integ.enabled ? 0 : 1;
      await api.put(`/admin/integrations-catalog/${integ.id}`, { enabled: newEnabled });
      showToast(true, `${integ.label} ${newEnabled ? 'ativada' : 'desativada'}`);
      await load();
    } catch (e: any) {
      showToast(false, 'Erro: ' + (e?.message || 'desconhecido'));
    }
  }

  async function handleDelete(integ: Integration) {
    if (!confirm(`Desativar "${integ.label}"?`)) return;
    try {
      await api.delete(`/admin/integrations-catalog/${integ.id}`);
      showToast(true, `${integ.label} desativada`);
      await load();
    } catch (e: any) {
      showToast(false, 'Erro: ' + (e?.message || 'desconhecido'));
    }
  }

  // Group by category
  const filtered = integrations.filter(i => {
    if (showFavoritesOnly && !i.is_favorite) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return i.label.toLowerCase().includes(q) || i.id.toLowerCase().includes(q) || i.category.includes(q);
  });

  const grouped = CATEGORY_ORDER.map(cat => ({
    category: cat,
    label: CATEGORY_LABELS[cat] || cat,
    items: filtered.filter(i => i.category === cat),
  })).filter(g => g.items.length > 0);

  const isEditOpen = editing !== null || creating;

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: 0 }}>Integrações</h1>
          <p style={{ fontSize: 13, color: T.textMuted, margin: '4px 0 0' }}>
            {integrations.length} integrações cadastradas — {integrations.filter(i => i.enabled).length} ativas
          </p>
        </div>
        <Button variant="primary" onClick={startCreate}>
          + Adicionar Integração
        </Button>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          padding: '8px 16px', marginBottom: 16, borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: toast.ok ? T.successBg : T.dangerBg,
          color: toast.ok ? T.success : T.danger,
          border: `1px solid ${toast.ok ? T.successBg : T.dangerBg}`,
        }}>{toast.msg}</div>
      )}

      {/* Search */}
      <input
        type="search" placeholder="Buscar integração..."
        value={filter} onInput={(e: any) => setFilter(e.target.value)}
        style={{
          width: '100%', padding: '10px 14px', marginBottom: 20, borderRadius: 8,
          background: T.bgSecondary, border: `1px solid ${T.bgHover}`,
          color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box',
        }}
      />

      {/* Favorites filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <Button variant={showFavoritesOnly ? 'primary' : 'ghost'} size="sm" onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
          style={showFavoritesOnly ? { background: T.warningBg, color: T.warning, border: `1px solid ${T.warningBgStrong}` } : {}}>
 Mostrar apenas favoritos
        </Button>
      </div>

      {/* Edit/Create Modal */}
      {isEditOpen && (
        <div style={{
          marginBottom: 24, padding: 20, borderRadius: 12,
          background: T.bgSecondary, border: `1px solid ${T.bgHover}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.text }}>
              {creating ? i18next.t('admin:new_integration') : `Editando: ${editData.label}`}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => { setEditing(null); setCreating(false); }}>x</Button>
          </div>

          {/* SVG Preview */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 10,
              background: `${editData.color || T.textMuted}15`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `1px solid ${editData.color || T.textMuted}30`,
            }}>
              <SvgPreview path={editData.svg_path} color={editData.color || T.textMuted} size={28} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{editData.label || i18next.t('chat:no_name')}</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>{editData.id || 'sem-id'}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="ID (slug)" value={editData.id || ''} onChange={v => setEditData(d => ({ ...d, id: v }))} disabled={!creating} placeholder="ex: cloudflare" />
            <Field label="Label" value={editData.label || ''} onChange={v => setEditData(d => ({ ...d, label: v }))} placeholder="ex: Cloudflare" />
            <Field label="Categoria" value={editData.category || ''} onChange={v => setEditData(d => ({ ...d, category: v }))} placeholder="cloud, dev, messaging..." />
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Field label="Cor" value={editData.color || ''} onChange={v => setEditData(d => ({ ...d, color: v }))} placeholder="#F38020" />
              </div>
              <div style={{ width: 40, height: 40, borderRadius: 6, background: editData.color || T.textMuted, marginTop: 22, flexShrink: 0 }} />
            </div>
            <Field label="Website" value={editData.website || ''} onChange={v => setEditData(d => ({ ...d, website: v }))} placeholder="https://..." />
            {/* Docs URL, env_keys, test_command, how_to_get removed — credential details live in AgentCredentials.tsx */}
            <Field label="Sort Order" value={String(editData.sort_order ?? 0)} onChange={v => setEditData(d => ({ ...d, sort_order: parseInt(v) || 0 }))} placeholder="0" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 22 }}>
              <label style={{ fontSize: 12, color: T.textSecondary }}>Ativo:</label>
              <input type="checkbox" checked={!!editData.enabled} onChange={(e: any) => setEditData(d => ({ ...d, enabled: e.target.checked ? 1 : 0 }))} />
 <label style={{ fontSize: 12, color: T.warning, marginLeft: 8 }}>Favorito:</label>
              <input type="checkbox" checked={!!editData.is_favorite} onChange={(e: any) => setEditData(d => ({ ...d, is_favorite: e.target.checked ? 1 : 0 }))} />
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <Field label="Descrição" value={editData.description || ''} onChange={v => setEditData(d => ({ ...d, description: v }))} placeholder="Descrição curta..." />
          </div>

          <div style={{ marginTop: 12 }}>
            <FieldArea label="SVG Path (d=)" value={editData.svg_path || ''} onChange={v => setEditData(d => ({ ...d, svg_path: v }))} rows={2} placeholder="M12 2L2 7..." />
          </div>

          <div style={{ marginTop: 12 }}>
          </div>

          <div style={{ marginTop: 12 }}>
          </div>

          <div style={{ marginTop: 12 }}>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Button variant="primary" onClick={handleSave} disabled={saving} loading={saving}>{saving ? i18next.t('common:processing') : i18next.t('common:save')}</Button>
            <Button variant="secondary" onClick={() => { setEditing(null); setCreating(false); }}>Cancelar</Button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ padding: 40 }}><ListSkeleton rows={3} /></div>
      )}

      {/* List grouped by category */}
      {!loading && grouped.map(group => (
        <div key={group.category} style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: T.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {group.label} ({group.items.length})
          </h3>
          <div style={{ borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.border}` }}>
            {group.items.map((integ, i) => {
              let envKeysCount = 0;
              try { envKeysCount = JSON.parse(integ.env_keys || '[]').length; } catch {}
              // env_keys removed from DB — count not available here
              return (
                <div key={integ.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                  background: integ.enabled ? T.bgInput : T.bgOverlay,
                  borderBottom: i < group.items.length - 1 ? `1px solid ${T.bgCard}` : 'none',
                  opacity: integ.enabled ? 1 : 0.5,
                  cursor: 'pointer', transition: 'background 0.1s',
                }}
                  onClick={() => startEdit(integ)}
                  onMouseEnter={(e: any) => e.currentTarget.style.background = T.primaryBg}
                  onMouseLeave={(e: any) => e.currentTarget.style.background = integ.enabled ? T.bgInput : T.bgOverlay}
                >
                  {/* Icon */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                    background: `${integ.color}12`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <SvgPreview path={integ.svg_path} color={integ.color} size={20} />
                  </div>

                  {/* Name + desc */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 {!!integ.is_favorite && <span style={{ fontSize: 12 }} title="Favorito"></span>}
                      <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{integ.label}</span>
                      <CategoryBadge category={integ.category} />
                    </div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {integ.description || i18next.t('admin:no_description')}
                    </div>
                  </div>

                  {/* Color swatch */}
                  <div style={{ width: 16, height: 16, borderRadius: 4, background: integ.color, flexShrink: 0, border: `1px solid ${T.borderHover}` }} />

                  {/* Env keys count */}
                  <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0, minWidth: 50, textAlign: 'center' }}>
                    {envKeysCount} chave{envKeysCount !== 1 ? 's' : ''}
                  </span>

                  {/* Toggle */}
                  <div onClick={(e) => { e.stopPropagation(); handleToggle(integ); }} style={{
                    width: 36, height: 20, borderRadius: 10, cursor: 'pointer', flexShrink: 0,
                    background: integ.enabled ? T.successBg : T.bgHover,
                    position: 'relative', transition: 'background 0.2s',
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%', position: 'absolute', top: 2,
                      left: integ.enabled ? 18 : 2,
                      background: integ.enabled ? T.success : T.textMuted,
                      transition: 'left 0.2s, background 0.2s',
                    }} />
                  </div>

                  {/* Order */}
                  <span style={{ fontSize: 10, color: T.textMuted, flexShrink: 0, width: 20, textAlign: 'center' }}>
                    #{integ.sort_order}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>Nenhuma integração encontrada.</div>
      )}
    </div>
  );
}

// ─── Form helpers ──────────────────────────────────────────────────────
function Field({ label, value, onChange, placeholder, disabled }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean;
}) {
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, display: 'block', marginBottom: 4 }}>{label}</label>
      <input
        type="text" value={value} onInput={(e: any) => onChange(e.target.value)}
        placeholder={placeholder} disabled={disabled}
        style={{
          width: '100%', padding: '7px 10px', borderRadius: 6, boxSizing: 'border-box',
          background: disabled ? T.bgSubtle : T.bg,
          border: `1px solid ${T.bgHover}`, color: T.text, fontSize: 12,
          outline: 'none', opacity: disabled ? 0.5 : 1,
        }}
      />
    </div>
  );
}

function FieldArea({ label, value, onChange, placeholder, rows = 3 }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; rows?: number;
}) {
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, display: 'block', marginBottom: 4 }}>{label}</label>
      <Textarea
        value={value} onInput={(e: any) => onChange(e.target.value)}
        placeholder={placeholder} rows={rows}
        style={{
          width: '100%', padding: '7px 10px', borderRadius: 6, boxSizing: 'border-box',
          background: T.bg, border: `1px solid ${T.bgHover}`,
          color: T.text, fontSize: 12, fontFamily: 'monospace', outline: 'none', resize: 'vertical',
        }}
      />
    </div>
  );
}
